import { custom } from "./actionsUtilities";
import { success } from "./actionsUtilities";
import { authenticationService } from "../../services";
import {
  authenticationConstants,
  EMPTY_STRING,
  routesPathConstants,
  systemConstants,
  toastMessages,
  toastType,
  transactionConstants,
} from "../../constants";
import { clearStorage, removeItemFromStorage } from "../../system/storage";
import { getUserInfoFromToken } from "../../system/encryption";
import {
  customToast,
  isAdminLogin,
  isCompanyLogin,
  handleUnlockGuestCustomersInTrade,
  isProductsInStash,
} from "../../shared/utility";
import { systemActions } from "./system.action";

//-------Login function to handle user login
const companyLogin = (userLoginData) => {
  return (dispatch) => {
    dispatch(
      custom(
        authenticationConstants.EMPLOYEE_LOGIN,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    authenticationService
      .companyLogin(userLoginData)
      .then(
        (response) => {
          const userInfo = getUserInfoFromToken(response.accessToken);

          if (isAdminLogin(userInfo)) {
            dispatch(
              success(
                { ...response, user: userInfo },
                authenticationConstants.EMPLOYEE_LOGIN
              )
            );
          } else {
            dispatch(
              success(
                { ...response, company: userInfo },
                authenticationConstants.COMPANY_LOGIN
              )
            );
          }
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            authenticationConstants.EMPLOYEE_LOGIN,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

//-------Employee Login function to handle employee login
const employeeLogin = (userLoginData, companyId) => {
  return (dispatch) => {
    dispatch(
      custom(
        authenticationConstants.EMPLOYEE_LOGIN,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    authenticationService
      .employeeLogin(userLoginData, companyId)
      .then(
        (response) => {
          const user = getUserInfoFromToken(response.accessToken);
          dispatch(
            success(
              { ...response, user: user },
              authenticationConstants.EMPLOYEE_LOGIN
            )
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            authenticationConstants.EMPLOYEE_LOGIN,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

const employeeLogout = (isDispatch = true) => {
  const processLogout = () => {
    removeItemFromStorage(process.env.REACT_APP_PAYLOAD);
    window.location.href = routesPathConstants.DEFAULT_PATH;
  };
  if (isDispatch) {
    return (dispatch) => {
      processLogout();
    };
  } else {
    processLogout();
  }
};

//-------Company Logout Action
const companyLogout = () => {
  const processLogout = (dispatch) => {
    dispatch(
      custom(
        authenticationConstants.COMPANY_LOGOUT,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    // handle unlock guest customers of trade transactions (traades with no split payments);
    handleUnlockGuestCustomersInTrade();
    clearStorage();
    dispatch(success(false, authenticationConstants.COMPANY_LOGOUT));
    dispatch(
      custom(
        authenticationConstants.COMPANY_LOGOUT,
        systemConstants.SPINNER_DEACTIVATE
      )
    );
  };

  return (dispatch) => {
    processLogout(dispatch);
  };
};

//------forgot password
const forgotPassword = (data, companyId) => {
  return (dispatch) => {
    dispatch(
      custom(
        authenticationConstants.FORGOT_PASSWORD,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    authenticationService
      .forgotPassword(data, companyId)
      .then(
        (response) => {
          customToast(
            toastMessages.PASSWORD_RESET_LINK_SENT_SUCCESSFULLY,
            toastType.SUCCESS
          );
          dispatch(
            systemActions.pageRedireaction(routesPathConstants.DEFAULT_PATH)
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            authenticationConstants.FORGOT_PASSWORD,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

//------save password
const savePassword = (data, role) => {
  return (dispatch) => {
    dispatch(
      custom(
        authenticationConstants.RESET_PASSWORD,
        systemConstants.SPINNER_ACTIVATE
      )
    );
    authenticationService
      .savePassword(data)
      .then(
        (response) => {
          customToast(
            isAdminLogin(role) || isCompanyLogin(role)
              ? toastMessages.PASSWORD_UPDATED_SUCCESSFULLY
              : toastMessages.PIN_UPDATED_SUCCESSFULLY,
            toastType.SUCCESS
          );
          dispatch(
            systemActions.pageRedireaction(routesPathConstants.DEFAULT_PATH)
          );
        },
        (error) => {
          customToast(error, toastType.ERROR);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            authenticationConstants.RESET_PASSWORD,
            systemConstants.SPINNER_DEACTIVATE
          )
        );
      });
  };
};

//------save password
const isLinkExpired = (data) => {
  return (dispatch) => {
    authenticationService.isLinkExpired(data).then(
      (response) => {},
      (error) => {
        customToast(error, toastType.ERROR);
        dispatch(
          systemActions.pageRedireaction(routesPathConstants.DEFAULT_PATH)
        );
      }
    );
  };
};

// This makes sure to show confirmation modal if there are inventory in local storage (sale, trade, batch) and if user agrees than clear the cart and process logout
const handleUserDropdownLogout = (user) => {
  return (dispatch) => {
    const transactionWithInvInCart = isProductsInStash();

    const processLogout = () => {
      removeItemFromStorage(transactionConstants.NEW_SALE_PAGE_DATA, true);
      removeItemFromStorage(transactionConstants.NEW_TRADE_PAGE_DATA, true);
      removeItemFromStorage(transactionConstants.TRADE_BATCH_DATA, true);
      if (isAdminLogin(user)) {
        dispatch(companyLogout());
      } else {
        dispatch(employeeLogout());
      }
    };

    // only show confirmation modal if there are items in cart (sale, trade, add batch inventory etc)
    if (transactionWithInvInCart) {
      const { storeName, inventoryType } = transactionWithInvInCart;

      const cartDescription = inventoryType
        ? `You have items in your ${inventoryType} cart.`
        : "You have items in your cart.";

      const initialMessage = storeName
        ? `${cartDescription} Check all stores, for example "${storeName}".`
        : `${cartDescription} Check all stores.`;

      dispatch(
        custom(
          {
            isShow: true,
            message: `${initialMessage} Logging out will reset all carts, and some data may be lost. Are you sure you want to log out?`,
            handleYesButtonClick: {
              functionAttributes: [dispatch],
              functionToCall: processLogout,
              isResetModalOnYesButtonClick: true,
            },
          },
          systemConstants.SET_CONFIRMATION_MODAL_REDUX
        )
      );
    } else {
      processLogout();
    }
  };
};

//-------Export Authentication Actions
export const authenticationActions = {
  companyLogin,
  employeeLogin,
  companyLogout,
  employeeLogout,
  forgotPassword,
  savePassword,
  isLinkExpired,
  handleUserDropdownLogout,
};
